import { PDFDocument } from '@cantoo/pdf-lib'
import axios from 'axios'
import FileService from '@/modules/file/fileService'

interface MergeOptions {
  httpHeaders: Record<string, string>
  dmsFileUrl?: string
}

export async function mergePdfDocuments(
  originalData: string | Blob,
  signatureDoc: { url?: string; minioId?: string },
  options: MergeOptions,
): Promise<Blob> {
  // 1. Get original PDF as ArrayBuffer
  let originalArrayBuffer: ArrayBuffer
  if (originalData instanceof Blob) {
    originalArrayBuffer = await originalData.arrayBuffer()
  } else if (typeof originalData === 'string') {
    const response = await fetch(originalData)
    const blob = await response.blob()
    originalArrayBuffer = await blob.arrayBuffer()
  }

  // 2. Fetch signature PDF
  let signatureArrayBuffer: ArrayBuffer
  if (signatureDoc.minioId && options.dmsFileUrl) {
    const signatureData = await FileService.streamDmsFile(signatureDoc.minioId)
    const response = await fetch(signatureData)
    const blob = await response.blob()
    signatureArrayBuffer = await blob.arrayBuffer()
  } else if (signatureDoc.url) {
    const response = await axios.get(signatureDoc.url, {
      headers: options.httpHeaders,
      responseType: 'arraybuffer',
    })
    signatureArrayBuffer = response.data
  } else {
    throw new Error('No valid signature document source')
  }

  // 3. Load both PDFs with pdf-lib
  const originalPdf = await PDFDocument.load(originalArrayBuffer)
  const signaturePdf = await PDFDocument.load(signatureArrayBuffer)

  // 4. Create merged PDF
  const mergedPdf = await PDFDocument.create()

  // Copy all pages from original
  const originalPages = await mergedPdf.copyPages(
    originalPdf,
    originalPdf.getPageIndices(),
  )
  originalPages.forEach(page => mergedPdf.addPage(page))

  // Copy all pages from signature
  const signaturePages = await mergedPdf.copyPages(
    signaturePdf,
    signaturePdf.getPageIndices(),
  )
  signaturePages.forEach(page => mergedPdf.addPage(page))

  // 5. Save as blob
  const mergedPdfBytes = await mergedPdf.save()
  // Convert Uint8Array to ArrayBuffer for Blob compatibility
  const arrayBuffer = mergedPdfBytes.buffer.slice(
    mergedPdfBytes.byteOffset,
    mergedPdfBytes.byteOffset + mergedPdfBytes.byteLength,
  )
  return new Blob([arrayBuffer], { type: 'application/pdf' })
}
